import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Key, User, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "", email: "", setup_key: "" });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.setup_key) {
      toast.error("Fyll ut brukernavn, passord og setup-nokkel");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, form);
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_user", JSON.stringify(res.data.admin));
      toast.success("Admin-konto opprettet!");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registrering feilet");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 data-testid="admin-register-title" className="font-serif text-4xl tracking-tight mb-2">Imassasje</h1>
          <p className="text-muted-foreground text-sm">Registrer ny admin</p>
        </div>
        <form onSubmit={handleRegister} className="space-y-6">
          <div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Key size={14} /> Setup-nokkel *</label>
            <Input
              data-testid="register-setup-key"
              type="password"
              value={form.setup_key}
              onChange={e => setForm({ ...form, setup_key: e.target.value })}
              placeholder="Hemmelig nokkel"
              className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><User size={14} /> Brukernavn *</label>
            <Input
              data-testid="register-username"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              placeholder="Velg et brukernavn"
              className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Mail size={14} /> E-post</label>
            <Input
              data-testid="register-email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="din@epost.no (valgfritt)"
              className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Lock size={14} /> Passord *</label>
            <Input
              data-testid="register-password"
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="Velg et sterkt passord"
              className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
            />
          </div>
          <Button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base transition-all hover:scale-105 w-full"
          >
            {loading ? "Registrerer..." : "Opprett admin-konto"}
          </Button>
        </form>
        <p className="text-center mt-6 text-sm text-muted-foreground">
          Har du allerede en konto?{" "}
          <Link to="/admin" data-testid="register-login-link" className="text-foreground hover:underline">Logg inn</Link>
        </p>
      </div>
    </div>
  );
}
